import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./AddSubscription.css";
import { APIs } from "../../../../../../util/api/APIs";
import SafeairManager from "../../../../../../util/api/SafeairManager";
import { useLocation } from "react-router-dom";
import { useStore } from "../../../../../../Hooks/useStore";
const SubscriptionForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const orgId = location.search?.substring(1); // extract orgId from query string (after '?')

    const today = new Date().toISOString().split("T")[0];
    const nextYear = new Date();
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    const oneYearLater = nextYear.toISOString().split("T")[0];

    const [formData, setFormData] = useState({
        startDate: today,
        endDate: oneYearLater,
        isActive: true,
        status: "",
    });

    const [subscriptionExists, setSubscriptionExists] = useState(true);
    const { rootStore } = useStore();


    useEffect(() => {
        const fetchSubscription = async () => {
            try {
                rootStore.loadingStore.setShowLoader(true); // Show loader before API call
    
                const result = await SafeairManager.getSubscriptionByOrgId({
                    api: APIs.Form.getSubscriptionByOrgId,
                    parameters: { orgId },
                });
    
                console.log("Fetched subscription:", result);
    
                const convertToISO = (dateStr) => {
                    const [day, month, year] = dateStr.split("/");
                    return `${year}-${month}-${day}`;
                };
    
                setFormData({
                    startDate: result.startDate ? convertToISO(result.startDate) : today,
                    endDate: result.endDate ? convertToISO(result.endDate) : oneYearLater,
                    isActive: result.active,
                    status: result.status || "",
                });
    
                setSubscriptionExists(true);
            } catch (error) {
                console.error("Failed to fetch subscription:", error);
    
                const message = error?.response?.data?.errorMessage;
                if (
                    error?.response?.status === 400 &&
                    message?.includes("Subscription for this organization does not exist")
                ) {
                    setFormData({
                        startDate: today,
                        endDate: oneYearLater,
                        isActive: true,
                    });
                    setSubscriptionExists(false);
                }
            } finally {
                rootStore.loadingStore.setShowLoader(false); // Hide loader after API call
            }
        };
    
        if (orgId) {
            fetchSubscription();
        }
    }, [orgId]);
    
      


    useEffect(() => {
        if (formData.isActive) {
            const startDate = new Date(formData.startDate);
            const newEndDate = new Date(startDate);
            newEndDate.setFullYear(startDate.getFullYear() + 1);
            setFormData(prev => ({
                ...prev,
                endDate: newEndDate.toISOString().split("T")[0]
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                endDate: today
            }));
        }
    }, [formData.startDate, formData.isActive]);

    const handleChange = (e) => {
        const { name, type, value, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleToggle = (status) => {
        setFormData((prev) => {
            const updated = {
                ...prev,
                isActive: status,
            };
    
            // Automatically set endDate to today when canceling
            if (!status && prev.isActive) {
                updated.endDate = today;
            }
    
            return updated;
        });
    };

    const handleSave = async () => {
        const formattedStartDate = new Date(formData.startDate).toLocaleDateString("en-GB"); // format: dd/mm/yyyy
    
        const body = {
            orgId: parseInt(orgId),
            isActive: formData.isActive,
        };
    
        if (formData.isActive) {
            body.startDate = formattedStartDate;
        }
    
        const apiConfig = {
            api: APIs.Form.saveSubscriptionByOrgId,
            parameters: {},
            body: JSON.stringify(body),
        };
    
        try {
            const res = await SafeairManager.saveSubscription(apiConfig);
            if (res) {
                rootStore.notificationToastStore.showNotification(
                    "Subscription saved successfully!",
                    false, // false = not an error
                    3000
                );
                navigate(-1);
            }
        } catch (error) {
            console.error("Failed to save subscription", error);
    
            const apiMessage =
                error?.response?.data?.errorMessage ||
                "Failed to save subscription."; // fallback message
    
            rootStore.notificationToastStore.showNotification(
                apiMessage,
                true, // true = is an error
                3000
            );

            
        }
    };


    const getStatusColor = (status) => {
        switch (status.toUpperCase()) {
          case "ACTIVE":
            return "#28a745"; // green
          case "CANCELLED":
            return "#dc3545"; // red
          case "EXPIRED":
            return "#ffc107"; // yellow
          case "RENEWED":
            return "#007bff"; // blue
          default:
            return "#6c757d"; // gray
        }
      };
    

    return (
        <div className="subscription-container">
            <div className="subscription-card">
                <div className="subscription-header">
                <h2 className="subscription-title" style={{ display: "flex", alignItems: "center", gap: "10px", color: "#ffffff" }}>
                    Subscription

                    {formData.status && (
                        <span
                        style={{
                            padding: "4px 10px",
                            borderRadius: "12px",
                            backgroundColor: getStatusColor(formData.status),
                            color: "#fff",
                            fontSize: "12px",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                        }}
                        >
                        {formData.status}
                        </span>
                    )}
                    </h2>
                    <button className="back-button" onClick={() => navigate(-1)}>
                        <span className="back-icon">&larr;</span> Back
                    </button>
                </div>

                <div className="subscription-form">
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">Start Date</label>
                            <div className="date-input-container">
                                <input
                                    type="date"
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleChange}
                                    className="form-input date-input"
                                    readOnly={!formData.isActive}
                                />
                                <span className="calendar-icon">📅</span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">End Date</label>
                            <div className="date-input-container">
                                <input
                                    type="date"
                                    name="endDate"
                                    value={formData.endDate}
                                    className="form-input date-input"
                                    readOnly
                                />
                                <span className="calendar-icon">📅</span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Manage Subscription</label>
                            <div className="toggle-container">
                                <div className="toggle-group">
                                <button
                                        type="button"
                                        className={`toggle-btn ${!formData.isActive ? "active" : ""}`}
                                        onClick={() => handleToggle(false)}
                                        disabled={!subscriptionExists} // <-- added this line
                                    >
                                        Cancel Subscription
                                    </button>
                                    <button
                                        type="button"
                                        className={`toggle-btn ${formData.isActive ? "active" : ""}`}
                                        onClick={() => handleToggle(true)}
                                    >
                                        Start Subscription
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        {/*
                        <button className="cancel-button" onClick={() => navigate(-1)}>
                            Cancel
                        </button>
                        */}
                        <button className="save-button" onClick={handleSave}>
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionForm;
